/**
 * DemoScripts – Comprehensive auto-play step definitions.
 *
 * Each script defines an ordered sequence of steps with:
 *  - Action type (NAVIGATE, CLICK, WAIT, HIGHLIGHT, FORM_SUBMIT, API_CALL)
 *  - Target CSS selector (for CLICK/HIGHLIGHT)
 *  - Route (for NAVIGATE)
 *  - Delay before auto-advancing
 *  - Tooltip text shown during the step
 */

import type { DemoEmotion } from './DemoNarrationService';

export type DemoScriptAction =
  | 'NAVIGATE'
  | 'CLICK'
  | 'WAIT'
  | 'HIGHLIGHT'
  | 'FORM_SUBMIT'
  | 'API_CALL'
  | 'WHAT_IF';

export interface DemoScriptStep {
  id: string;
  title: string;
  description: string;
  action: DemoScriptAction;
  /** CSS selector for the target element. */
  target?: string;
  /** Route to navigate to. Supports :programId and :patientId placeholders. */
  route?: string;
  /** Base delay in ms before auto-advancing. Divided by speed multiplier. */
  delayMs: number;
  /** Optional payload for FORM_SUBMIT, API_CALL, or WHAT_IF actions. */
  payload?: Record<string, unknown>;
  /** Badge label shown on the tooltip. */
  badge?: string;
  /** Voice narration text spoken when this step starts (SpeechSynthesis). */
  narration?: string;
  /**
   * Emotion-based delivery style for the narration. Defaults to CALM.
   * - CALM: dashboards, summary, settings
   * - EXCITED: lists, search, filtering, results
   * - EMPHASIS_HEAVY: financial / utilization figures
   * - STORYTELLING: Patient360 / case history walkthroughs
   * - ALERT: approvals, breaches, workflow transitions
   */
  emotion?: DemoEmotion;
}

export interface DemoScript {
  id: string;
  name: string;
  description: string;
  /** Role filter: 'CC', 'CCM', or 'ALL'. */
  role: 'CC' | 'CCM' | 'ALL';
  steps: DemoScriptStep[];
}

// ─── CARE COORDINATOR AUTO-PLAY ─────────────────────────

const CC_AUTOPLAY: DemoScriptStep[] = [
  {
    id: 'cc-auto-1',
    title: 'Navigate to Dashboard',
    description: 'Opening the program workspace dashboard — your central hub for patient management and KPIs.',
    action: 'NAVIGATE',
    route: '/programs/:programId/workspace',
    delayMs: 3000,
    badge: 'Dashboard',
    emotion: 'CALM',
    narration: 'Welcome to the [em]Care Coordinator[/em] demo. Let us start at the [em]workspace dashboard[/em], your central hub for managing patients and tracking key metrics.',
  },
  {
    id: 'cc-auto-2',
    title: 'KPI Summary',
    description: 'The KPI strip shows real-time counts: total patients, active cases, pending cases, and call log activity.',
    action: 'HIGHLIGHT',
    target: '[data-demo="kpi-strip"]',
    delayMs: 4000,
    badge: 'KPIs',
    emotion: 'EMPHASIS_HEAVY',
    narration: 'At the top, the [em]KPI strip[/em] gives you a real-time snapshot of total patients, active cases, pending items, and call log activity.',
  },
  {
    id: 'cc-auto-3',
    title: 'Patient List',
    description: 'The patient table lists all enrolled patients with search, filter, and sort capabilities.',
    action: 'HIGHLIGHT',
    target: '[data-demo="patient-table"]',
    delayMs: 3500,
    badge: 'Patients',
    emotion: 'EXCITED',
  },
  {
    id: 'cc-auto-4',
    title: 'Opening First Patient',
    description: 'Clicking the first patient row to open their full Patient 360 profile.',
    action: 'CLICK',
    target: '[data-demo="patient-row-first"]',
    delayMs: 2000,
    badge: 'Navigate',
    emotion: 'EXCITED',
  },
  {
    id: 'cc-auto-5',
    title: 'Patient Details Overview',
    description: 'Patient Details consolidates demographics, case history, call logs, documents, and the treatment timeline.',
    action: 'WAIT',
    delayMs: 4000,
    badge: 'Patient Details',
    emotion: 'STORYTELLING',
  },
  {
    id: 'cc-auto-6',
    title: 'Case History Section',
    description: 'The assistance case history shows all cases for this patient with status, enrollment date, and amounts.',
    action: 'HIGHLIGHT',
    target: '[data-demo="case-history"]',
    delayMs: 3500,
    badge: 'Cases',
    emotion: 'STORYTELLING',
  },
  {
    id: 'cc-auto-7',
    title: 'Create Case Button',
    description: 'The "Create Case" button opens a dynamic enrollment form driven by the published case template.',
    action: 'HIGHLIGHT',
    target: '[data-demo="create-case-btn"]',
    delayMs: 3000,
    badge: 'Create Case',
    emotion: 'ALERT',
  },
  {
    id: 'cc-auto-8',
    title: 'Call Log Section',
    description: 'Call logs track all patient interactions — type, status, subject, notes, and follow-up scheduling.',
    action: 'HIGHLIGHT',
    target: '[data-demo="call-log-section"]',
    delayMs: 3500,
    badge: 'Call Logs',
    emotion: 'CALM',
  },
  {
    id: 'cc-auto-9',
    title: 'Back to Workspace',
    description: 'Returning to the workspace to continue the patient management flow.',
    action: 'NAVIGATE',
    route: '/programs/:programId/workspace',
    delayMs: 2500,
    badge: 'Workspace',
    emotion: 'CALM',
  },
  {
    id: 'cc-auto-10',
    title: 'Demo Complete',
    description: 'The Care Coordinator auto-play demo is complete. You can now explore the workspace, manage patients, create cases, and log calls.',
    action: 'WAIT',
    delayMs: 3000,
    badge: 'Done',
    emotion: 'CALM',
  },
];

// ─── CARE CASE MANAGER AUTO-PLAY ────────────────────────

const CCM_AUTOPLAY: DemoScriptStep[] = [
  {
    id: 'ccm-auto-1',
    title: 'Navigate to Workspace',
    description: 'Opening the management workspace — approval queues, KPIs, and team oversight.',
    action: 'NAVIGATE',
    route: '/programs/:programId/workspace',
    delayMs: 3000,
    badge: 'Dashboard',
    emotion: 'CALM',
    narration: 'Welcome to the [em]Care Case Manager[/em] demo. This workspace gives you oversight of approval queues, program KPIs, and team performance.',
  },
  {
    id: 'ccm-auto-2',
    title: 'Program KPIs',
    description: 'Program-wide KPIs show case volumes, approval rates, pending actions, and financial utilization.',
    action: 'HIGHLIGHT',
    target: '[data-demo="kpi-strip"]',
    delayMs: 4000,
    badge: 'KPIs',
    emotion: 'EMPHASIS_HEAVY',
    narration: 'Program-wide [em]KPIs[/em] show case volumes, approval rates, pending actions, and [em]financial utilization[/em] at a glance.',
  },
  {
    id: 'ccm-auto-3',
    title: 'Approval Queue',
    description: 'Cases awaiting your approval are surfaced here. Each row shows patient, priority, and time since submission.',
    action: 'HIGHLIGHT',
    target: '[data-demo="approval-queue"]',
    delayMs: 4000,
    badge: 'Approvals',
    emotion: 'ALERT',
  },
  {
    id: 'ccm-auto-4',
    title: 'Opening Case for Review',
    description: 'Clicking a pending case to review patient details, treatment plan, and financial impact.',
    action: 'CLICK',
    target: '[data-demo="case-row-first"]',
    delayMs: 2000,
    badge: 'Review',
    emotion: 'ALERT',
  },
  {
    id: 'ccm-auto-5',
    title: 'Case Detail View',
    description: 'The full case detail shows enrollment info, benefit plan, cycle status, and workflow history.',
    action: 'WAIT',
    delayMs: 4000,
    badge: 'Case Detail',
    emotion: 'STORYTELLING',
  },
  {
    id: 'ccm-auto-6',
    title: 'Approval Actions',
    description: 'Approve or reject with required comments. All decisions are audit-logged with timestamp and user.',
    action: 'HIGHLIGHT',
    target: '[data-demo="approve-reject-btns"]',
    delayMs: 3500,
    badge: 'Decision',
    emotion: 'ALERT',
  },
  {
    id: 'ccm-auto-7',
    title: 'Navigate to Reports',
    description: 'Opening the reports dashboard for utilization trends, approval TAT, and financial summaries.',
    action: 'NAVIGATE',
    route: '/programs/:programId/reports',
    delayMs: 3000,
    badge: 'Reports',
    emotion: 'CALM',
  },
  {
    id: 'ccm-auto-8',
    title: 'Report Dashboard',
    description: 'Reports include patient lists, case status breakdowns, utilization metrics, and turnaround analysis.',
    action: 'HIGHLIGHT',
    target: '[data-demo="report-dashboard"]',
    delayMs: 4000,
    badge: 'Analytics',
    emotion: 'CALM',
  },
  {
    id: 'ccm-auto-9',
    title: 'Demo Complete',
    description: 'The Care Case Manager auto-play demo is complete. You can manage approvals, monitor KPIs, and generate reports.',
    action: 'WAIT',
    delayMs: 3000,
    badge: 'Done',
    emotion: 'CALM',
  },
];

// ─── FULL PROGRAM OVERVIEW (ALL ROLES) ──────────────────

const FULL_AUTOPLAY: DemoScriptStep[] = [
  {
    id: 'full-auto-1',
    title: 'Program Dashboard',
    description: 'Starting at the program dashboard — an overview of the setup checklist, activation status, and program health.',
    action: 'NAVIGATE',
    route: '/programs/:programId/dashboard',
    delayMs: 3500,
    badge: 'Overview',
    emotion: 'CALM',
    narration: 'Welcome to the full program overview. We begin at the program dashboard, showing the setup checklist, activation status, and overall program health.',
  },
  {
    id: 'full-auto-2',
    title: 'Workspace',
    description: 'The unified workspace consolidates patient, case, and entity management for all operational roles.',
    action: 'NAVIGATE',
    route: '/programs/:programId/workspace',
    delayMs: 3500,
    badge: 'Workspace',
    emotion: 'CALM',
  },
  {
    id: 'full-auto-3',
    title: 'KPI Summary',
    description: 'Real-time KPIs adapt to the logged-in user\'s role, showing relevant metrics and action items.',
    action: 'HIGHLIGHT',
    target: '[data-demo="kpi-strip"]',
    delayMs: 4000,
    badge: 'KPIs',
    emotion: 'EMPHASIS_HEAVY',
  },
  {
    id: 'full-auto-4',
    title: 'Entity Table',
    description: 'The main data table supports dynamic columns driven by form field configuration and entity field index.',
    action: 'HIGHLIGHT',
    target: '[data-demo="patient-table"]',
    delayMs: 3500,
    badge: 'Data',
    emotion: 'EXCITED',
  },
  {
    id: 'full-auto-5',
    title: 'Reports',
    description: 'Navigating to the reporting module — configurable report definitions with scheduled execution.',
    action: 'NAVIGATE',
    route: '/programs/:programId/reports',
    delayMs: 3500,
    badge: 'Reports',
    emotion: 'CALM',
  },
  {
    id: 'full-auto-6',
    title: 'Demo Complete',
    description: 'Program overview complete. Explore individual role walkthroughs for deeper feature demonstrations.',
    action: 'WAIT',
    delayMs: 3000,
    badge: 'Done',
    emotion: 'CALM',
  },
];

// ─── WHAT-IF SIMULATION DEMO ────────────────────────────

const WHATIF_AUTOPLAY: DemoScriptStep[] = [
  {
    id: 'whatif-1',
    title: 'Open Workspace',
    description: 'Starting at the workspace to review current KPIs before running simulations.',
    action: 'NAVIGATE',
    route: '/programs/:programId/workspace',
    delayMs: 3000,
    badge: 'Workspace',
    emotion: 'CALM',
    narration: 'Let us explore the what-if simulation engine. First, we will review the current KPIs on the workspace.',
  },
  {
    id: 'whatif-2',
    title: 'Highlight Current KPIs',
    description: 'These are the live KPI values based on actual program data.',
    action: 'HIGHLIGHT',
    target: '[data-demo="kpi-strip"]',
    delayMs: 4000,
    badge: 'Baseline',
    emotion: 'EMPHASIS_HEAVY',
    narration: 'These are your live KPI values. Now let us simulate what happens if we increase utilization by twenty percent.',
  },
  {
    id: 'whatif-3',
    title: 'What-If: Increase Utilization',
    description: 'Simulating a 20% increase in drug utilization across all cases.',
    action: 'WHAT_IF',
    delayMs: 5000,
    badge: 'Simulation',
    emotion: 'EMPHASIS_HEAVY',
    narration: 'We are now running a what-if simulation: increasing utilized amounts by twenty percent. Notice how the KPI projections update in real time.',
    payload: {
      presetId: 'increase-utilization',
      fieldKey: 'utilized_amount',
      operation: 'PERCENT_INCREASE',
      value: 20,
    },
  },
  {
    id: 'whatif-4',
    title: 'What-If: Improve Follow-Up',
    description: 'Simulating a 30% improvement in follow-up completion rate.',
    action: 'WHAT_IF',
    delayMs: 5000,
    badge: 'Simulation',
    emotion: 'EXCITED',
    narration: 'Next, let us simulate improving follow-up completion by thirty percent. This helps estimate the impact of better patient engagement.',
    payload: {
      presetId: 'improve-followup',
      fieldKey: 'followup_completion_rate',
      operation: 'PERCENT_INCREASE',
      value: 30,
    },
  },
  {
    id: 'whatif-5',
    title: 'Demo Complete',
    description: 'The what-if simulation demo is complete. You can open the panel and try different scenarios.',
    action: 'WAIT',
    delayMs: 3000,
    badge: 'Done',
    emotion: 'CALM',
    narration: 'The what-if simulation demo is complete. You can open the panel anytime to explore different scenarios without affecting live data.',
  },
];

// ─── SCRIPT REGISTRY ────────────────────────────────────

export const DEMO_SCRIPTS: DemoScript[] = [
  {
    id: 'cc-autoplay',
    name: 'Care Coordinator Auto-Play',
    description: 'Hands-free walkthrough of the CC workflow: patients, cases, and call logs.',
    role: 'CC',
    steps: CC_AUTOPLAY,
  },
  {
    id: 'ccm-autoplay',
    name: 'Care Case Manager Auto-Play',
    description: 'Hands-free walkthrough of the CCM workflow: approvals, dashboards, and reports.',
    role: 'CCM',
    steps: CCM_AUTOPLAY,
  },
  {
    id: 'full-autoplay',
    name: 'Full Program Overview',
    description: 'High-level tour covering dashboard, workspace, entities, and reports.',
    role: 'ALL',
    steps: FULL_AUTOPLAY,
  },
  {
    id: 'whatif-autoplay',
    name: 'What-If Simulation Demo',
    description: 'Explore hypothetical scenarios and see projected KPI impact in real time.',
    role: 'ALL',
    steps: WHATIF_AUTOPLAY,
  },
];

// ─── HELPERS ────────────────────────────────────────────

export function getScriptById(id: string): DemoScript | undefined {
  return DEMO_SCRIPTS.find((s) => s.id === id);
}

export function getScriptsForRole(roleName: string): DemoScript[] {
  const normalized = roleName.toLowerCase();
  const isCC = normalized.includes('care coordinator') && !normalized.includes('manager');
  const isCCM = normalized.includes('case manager') || normalized.includes('ccm');
  return DEMO_SCRIPTS.filter((s) => {
    if (s.role === 'ALL') return true;
    if (s.role === 'CC' && isCC) return true;
    if (s.role === 'CCM' && isCCM) return true;
    return false;
  });
}

export function resolveScriptRoute(route: string | undefined, programId: string, patientId?: string): string | null {
  if (!route) return null;
  let resolved = route.replace(':programId', programId);
  if (patientId) resolved = resolved.replace(':patientId', patientId);
  return resolved;
}
